import UserSessionFunctions from "@/functions/userSessionFunctions";
import UnlockMoreService from "@/services/unlockMoreService";
import store from "@/store";
import {handleMyBundleEntry, isMyBundleEntry, redirectToMyBundleLanding} from "@/functions/myBundleEntry";

const VIP_SDP_SERVICE_ID = 361;
const VIP_ENTERPRISE_SERVICE_ID = 612;
const VIP_DEP_SERVICE_ID = '11147';
const VIP_ENTITLED_STATUSES = new Set(['ACTIVE', 'PENDING', 'GRACE']);

export function deriveIsVip(identity) {
    if (!identity) return false;
    const status = String(identity.statusName || identity.StatusName || identity.Status || '').toUpperCase();
    if (!VIP_ENTITLED_STATUSES.has(status)) return false;
    const serviceName = String(identity.Service || identity.ServiceName || identity.serviceName || '');
    const serviceId = Number(identity.serviceId ?? identity.ServiceId);
    const externalId = String(identity.ExternalProductId ?? identity.externalProductId ?? '');
    return /vip/i.test(serviceName)
        || serviceId === VIP_SDP_SERVICE_ID
        || serviceId === VIP_ENTERPRISE_SERVICE_ID
        || externalId === VIP_DEP_SERVICE_ID;
}

function hydrateUserState(userData) {
    if (!userData) return;
    const pickNum = (...vals) => vals.find(v => typeof v === 'number' && Number.isFinite(v));
    store.commit('setUser', userData);

    const funds = userData.funds || {};

    store.commit('setIsVip', deriveIsVip(userData.user) || store.state.myBundle);

    const plays = pickNum(userData.playsLeft, funds.PlaysLeft, userData.totalPlays);
    if (plays !== undefined) store.commit('setPlays', Math.max(0, plays));

    const stars = pickNum(userData.starBalance, funds.StarBalance, userData.totalStars, userData.stars);
    if (stars !== undefined) store.commit('setStars', Math.max(0, stars));

    if (typeof userData.crateProgress === 'number')  store.commit('setCrateProgress', userData.crateProgress);
    if (typeof userData.streakCount === 'number')    store.commit('setStreakCount', userData.streakCount);
    if (userData.lastPlayedDate)                     store.commit('setLastPlayedDate', userData.lastPlayedDate);
    if (Array.isArray(userData.pendingCrates))       store.commit('setPendingCrates', userData.pendingCrates);

    const freeChests = pickNum(userData.totalFreeChests, funds.FreeChests);
    const vipChests  = pickNum(userData.totalPremiumChests, funds.PremiumChests);
    if (!Array.isArray(userData.pendingCrates) &&
        (freeChests !== undefined || vipChests !== undefined)) {
        const free = freeChests || 0;
        const vip = vipChests || 0;
        const crates = [];
        for (let i = 0; i < free; i++) crates.push({id: `free-${i}`, type: 'free'});
        for (let i = 0; i < vip; i++)  crates.push({id: `vip-${i}`,  type: 'vip'});
        store.commit('setPendingCrates', crates);
    }

    // Identity (statusName / serviceId / username) rides on the SAME getUser
    // response under userData.user (lucky-kite funds + merged DEP status), so a
    // single get-user feeds both the HUD and the subscription gate.
    const identity = userData.user || {};
    // Accept either casing: our SDP row is PascalCase (Username/StatusName); the
    // lowercase form only existed while get-user merged DEP's payload in.
    const identityUsername = identity.username ?? identity.Username;
    const identityStatus = identity.statusName ?? identity.StatusName;
    if (typeof identityUsername === 'string' && identityUsername.length > 0) {
        store.commit('setUsername', identityUsername);
    }
    if (typeof identityStatus === 'string') {
        store.commit('setSubscriptionStatus', identityStatus.toUpperCase());
    }
}

export async function refreshUserFromServer(msisdn) {
    try {
        const user = await UnlockMoreService.getUser(msisdn);
        hydrateUserState(user?.data);
        return true;
    } catch (e) {
        console.warn('[userAuthenticate] explicit refresh failed', e);
        return false;
    }
}

export async function advanceLoginStreak(msisdn) {
    if (!msisdn) return;
    try {
        const res = await UnlockMoreService.checkDailyStreak(msisdn);
        const row = Array.isArray(res?.data) ? res.data[0] : res?.data;
        if (!row) return;
        const day = Number(row.ClaimingStreakDay ?? row.claimingStreakDay);
        if (Number.isFinite(day)) store.commit('setStreakCount', day);
        const claimedAt = row.StreakClaimDate ?? row.streakClaimDate;
        if (claimedAt) store.commit('setLastPlayedDate', claimedAt);
    } catch (e) {
        console.warn('[advanceLoginStreak] check-daily-streak failed', e?.message || e);
    }
}

const AUTH_DISABLED = true;

const PUBLIC_PATHS = new Set([
    '/login/request-otp',
    '/login/submit-otp',
    '/terms-conditions',
    '/terms-and-conditions',
    '/how-it-works',
]);

function hasHydratedUser() {
    return !!store.state.user;
}

// force = re-hydrate even if a user object is already in the store. The My Bundle entry
// pre-commits a bare identity so there's a fallback if get-user fails, but that would
// otherwise short-circuit this and leave the app with no funds/plays/stars/isVip.
async function ensureUserHydrated(sessionMsisdn, force = false) {
    if (!force && hasHydratedUser()) return true;
    try {
        const user = await UnlockMoreService.getUser(sessionMsisdn);
        hydrateUserState(user?.data);
        await advanceLoginStreak(sessionMsisdn);
        return true;
    } catch (e) {
        console.warn('[userAuthenticate] initial getUser failed', e);
        return false;
    }
}

async function myBundleEntryFlow(to, from, next) {
    const entry = await handleMyBundleEntry();

    if (!entry.entered) {
        if (redirectToMyBundleLanding()) return false;
        return next('/login/request-otp');
    }

    await ensureUserHydrated(entry.user.msisdn, true);
    if (to.path === '/' || PUBLIC_PATHS.has(to.path)) return next('/home');
    return next();
}

export async function userAuthenticate(to, from, next) {
    if (isMyBundleEntry()) return await myBundleEntryFlow(to, from, next);

    const urlParams = new URLSearchParams(window.location.search);

    const passthroughMsisdn = (urlParams.get('msisdn') || '').trim();
    if (/^\+?\d{9,15}$/.test(passthroughMsisdn) && !UserSessionFunctions.getUserSesssionHasLoggedIn()) {
        UserSessionFunctions.setUserSesssionHasLoggedIn(true);
        UserSessionFunctions.setUserSesssionMsisdn(passthroughMsisdn);
        await ensureUserHydrated(passthroughMsisdn);
        if (to.path === '/' || PUBLIC_PATHS.has(to.path)) return next('/home');
        return next();
    }

    if (AUTH_DISABLED) {
        const sessionMsisdn = UserSessionFunctions.getUserSesssionMsisdn();
        if (UserSessionFunctions.getUserSesssionHasLoggedIn() && sessionMsisdn) {
            await ensureUserHydrated(sessionMsisdn);
            return next();
        }
        if (to.path === '/' || PUBLIC_PATHS.has(to.path)) return next();
        return next('/login/request-otp');
    }

    const encryptedMsisdn = urlParams.get('e-msisdn');

    if (to.path === '/') {
        if (encryptedMsisdn) {
            try {
                const response = await UnlockMoreService.headerEnrichmentLogin(encryptedMsisdn);
                if (response.data.error) return next('/login/request-otp');
                await UnlockMoreService.trackUserLogin(response.data);
                const user = await UnlockMoreService.getUser(response.data);
                UserSessionFunctions.setUserSesssionHasLoggedIn(true);
                UserSessionFunctions.setUserSesssionMsisdn(user.data.msisdn);
                hydrateUserState(user.data);
                await advanceLoginStreak(user.data.msisdn || response.data);
                return next('/home');
            } catch (e) {
                console.warn('[userAuthenticate] HE login failed, falling back to OTP', e);
                return next('/login/request-otp');
            }
        }
        return next();
    }

    if (PUBLIC_PATHS.has(to.path)) return next();

    if (UserSessionFunctions.getUserSesssionHasLoggedIn()) {
        const msisdn = UserSessionFunctions.getUserSesssionMsisdn();
        const fundsOk = await ensureUserHydrated(msisdn);
        if (!fundsOk && !hasHydratedUser()) {
            return next('/login/request-otp');
        }
        return next();
    }

    return next('/login/request-otp');
}
